/**
 * API 服务 - 基于公共 request 封装
 * 登录/注册不走 token，其余接口自动带 token
 */

import request from '../utils/request'

/** 登录请求体 */
export interface LoginParams {
  username: string
  password: string
}

/** 注册请求体 */
export interface RegisterParams {
  username: string
  email: string
  password: string
}

/** 接口返回的用户信息（按常见后端字段兼容） */
export interface ApiUser {
  id?: string | number
  username?: string
  name?: string
  email?: string
  nickname?: string
  phone?: string
  /** 角色编码列表，如 ["ADMIN"] */
  roles?: string[]
  avatarUrl?: string | null
  [key: string]: unknown
}

/** 登录接口 menuTree 节点（菜单与按钮权限树） */
export interface ApiMenuTreeNode {
  code: string
  name: string
  /** menu=导航菜单，button=按钮权限点 */
  type: 'menu' | 'button'
  path?: string
  permissionAction?: string
  children?: ApiMenuTreeNode[]
}

/** 登录接口 data 结构（新文档） */
export interface AuthLoginData {
  token: string
  expiresIn?: number
  user: ApiUser
  /** 当前用户可见的权限菜单树 */
  menuTree?: ApiMenuTreeNode[]
  /** 当前用户拥有的全部按钮权限 code 扁平列表，用于按钮显隐 */
  buttonPermissions?: string[]
}

/** 登录/注册返回（兼容新文档与旧格式） */
export interface AuthResponse {
  success?: boolean
  code?: string
  message?: string
  data?: AuthLoginData & { access_token?: string; user?: ApiUser; token?: string }
  access_token?: string
  token?: string
  user?: ApiUser
}

/** 后端 path 与前端路由映射（登录接口 menuTree 中 path 转前端路由） */
export const AUTH_MENU_PATH_MAP: Record<string, string> = {
  '/dashboard': '/',
  '/asset-products': '/assets',
  '/data-sources': '/data',
  '/analytics': '/analytics',
  '/settings': '/settings',
  '/account-management': '/accounts',
  '/role-management': '/roles',
  '/accounts': '/accounts',
  '/roles': '/roles',
}

/** 菜单 code 与前端路由映射（用于侧栏点击跳转） */
export const MENU_CODE_TO_PATH: Record<string, string> = {
  dashboard: '/',
  asset_products: '/assets',
  data_sources: '/data',
  analytics: '/analytics',
  settings: '/settings',
  settings_profile: '/settings/profile',
  settings_security: '/settings/security',
  user_management: '/accounts',
  role_management: '/roles',
  accounts: '/accounts',
  roles: '/roles',
}

// ========== 认证接口（不带头部 token，由 request 拦截器自动跳过） ==========

export const authApi = {
  /** 登录：POST /api/auth/login */
  login: (data: LoginParams) =>
    request.post<AuthResponse>('/api/auth/login', data).catch((e) => {
      throw e
    }),

  /** 注册：POST /api/auth/register */
  register: (data: RegisterParams) =>
    request.post<AuthResponse>('/api/auth/register', data).catch((e) => {
      throw e
    }),
}

// ========== 以下接口均自动携带 token ==========

/** 仪表盘统计/活动：改为使用新的 POST 接口 */
export const dashboardApi = {
  /** POST /api/dashboard/summary - 仪表盘汇总指标 */
  summary: (body?: Record<string, unknown>) =>
    request.post('/api/dashboard/summary', body ?? {}),
  /** POST /api/dashboard/distribution - 仪表盘资产分布 */
  distribution: (body?: Record<string, unknown>) =>
    request.post('/api/dashboard/distribution', body ?? {}),
  /** POST /api/dashboard/recent-activities - 仪表盘最近活动 */
  recentActivities: (body?: Record<string, unknown>) =>
    request.post('/api/dashboard/recent-activities', body ?? {}),
  /**
   * 兼容旧的 dashboardApi.getStats 调用：
   * 内部并行调用 summary + recent-activities，并组装为 { stats, recentActivities }
   */
  getStats: async (body?: Record<string, unknown>) => {
    const [summaryRes, activitiesRes, distributionRes] = await Promise.all([
      request.post('/api/dashboard/summary', body ?? {}),
      request.post('/api/dashboard/recent-activities', body ?? {}),
      request.post('/api/dashboard/distribution', body ?? {}),
    ])
    const stats = (summaryRes as any)?.data ?? summaryRes
    const activitiesRaw = (activitiesRes as any)?.data ?? activitiesRes
    const recentActivities = Array.isArray((activitiesRaw as any)?.items)
      ? (activitiesRaw as any).items
      : Array.isArray(activitiesRaw)
        ? activitiesRaw
        : []
    const distribution = (distributionRes as any)?.data ?? distributionRes
    return { stats, recentActivities, distribution }
  },
}

/** 资产列表请求参数：分页 + 筛选 + 排序 */
export interface AssetsListParams {
  page?: number
  pageSize?: number
  filters?: {
    keyword?: string
    type?: string
    risk?: string
    status?: string
  }
  sort?: {
    field?: string
    order?: string
  }
}

/** 资产产品：文档约定均为 POST */
export const assetApi = {
  /** POST /api/assets/list - 分页查询资产产品列表，带筛选与统计 */
  list: (body?: AssetsListParams | Record<string, unknown>) =>
    request.post('/api/assets/list', body ?? {}),
  /** POST /api/assets/detail - Asset Detail */
  detail: (body: { id: string } | Record<string, unknown>) => request.post('/api/assets/detail', body),
  /** POST /api/assets/create - Asset Create */
  create: (data: Record<string, unknown>) => request.post('/api/assets/create', data),
  /** POST /api/assets/update - Asset Update */
  update: (data: Record<string, unknown>) => request.post('/api/assets/update', data),
  /** POST /api/assets/delete - Asset Delete */
  delete: (body: { id: string } | Record<string, unknown>) => request.post('/api/assets/delete', body),
  /** POST /api/assets/linked-data-sources - 查询资产产品关联的资产数据列表（分页） */
  linkedDataSources: (body: {
    assetProductId: string
    page?: number
    pageSize?: number
    keyword?: string
    type?: string
    status?: string
  }) => request.post('/api/assets/linked-data-sources', body),
  /** POST /api/assets/link-data-sources - 创建资产产品与资产数据的关联关系 */
  linkDataSources: (body: { assetProductId: string; dataSourceIds: string[] }) =>
    request.post('/api/assets/link-data-sources', body),
  /** POST /api/assets/unlink-data-sources - 删除资产产品与资产数据的关联关系（仅删关联） */
  unlinkDataSources: (body: { assetProductId: string; dataSourceIds: string[] }) =>
    request.post('/api/assets/unlink-data-sources', body),
  /** 兼容旧调用：getAssets → list，支持分页筛选参数 */
  getAssets: (params?: AssetsListParams | Record<string, unknown>) =>
    request.post('/api/assets/list', params ?? {}),
  getAsset: (id: string) => request.post('/api/assets/detail', { id }),
  createAsset: (data: Record<string, unknown>) => request.post('/api/assets/create', data),
  updateAsset: (id: string, data: Record<string, unknown>) =>
    request.post('/api/assets/update', { ...data, id }),
  deleteAsset: (id: string) => request.post('/api/assets/delete', { id }),
}

/** 数据源：POST /api/data-sources/* （资产数据页使用） */
export const dataSourcesApi = {
  /** POST /api/data-sources/summary - 汇总统计 */
  summary: (body?: { filters?: { type?: string; status?: string } } | Record<string, unknown>) =>
    request.post('/api/data-sources/summary', body ?? {}),
  /** POST /api/data-sources/list - 数据源列表 */
  list: (body?: Record<string, unknown>) =>
    request.post('/api/data-sources/list', body ?? {}),
  /** POST /api/data-sources/detail - 数据源详情 */
  detail: (body: { id: string } | Record<string, unknown>) =>
    request.post('/api/data-sources/detail', body),
  /** POST /api/data-sources/delete - 删除数据源 */
  delete: (body: { id: string } | Record<string, unknown>) =>
    request.post('/api/data-sources/delete', body),
  /** POST /api/data-sources/export - 导出（返回 blob） */
  export: (body?: Record<string, unknown>) =>
    request.post('/api/data-sources/export', body ?? {}, { responseType: 'blob' }),
}

/** 资产数据：/api/data */
export const dataApi = {
  getData: (params?: Record<string, unknown>) => request.get('/api/data', { params }),
  importData: (formData: FormData) =>
    request.post('/api/data/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  exportData: (params?: Record<string, unknown>) =>
    request.get('/api/data/export', { params, responseType: 'blob' }),
  getStatistics: (params?: Record<string, unknown>) => request.get('/api/data/statistics', { params }),
}

/** 分析：文档约定均为 POST */
export const analyticsApi = {
  /** POST /api/analytics/indicators */
  indicators: (body?: Record<string, unknown>) => request.post('/api/analytics/indicators', body ?? {}),
  /** POST /api/analytics/asset-distribution */
  assetDistribution: (body?: Record<string, unknown>) => request.post('/api/analytics/asset-distribution', body ?? {}),
  /** POST /api/analytics/trend-analysis */
  trendAnalysis: (body?: Record<string, unknown>) => request.post('/api/analytics/trend-analysis', body ?? {}),
  /** POST /api/analytics/comparison-analysis */
  comparisonAnalysis: (body?: Record<string, unknown>) => request.post('/api/analytics/comparison-analysis', body ?? {}),
  /** POST /api/analytics/amount-dimension */
  amountDimension: (body?: Record<string, unknown>) => request.post('/api/analytics/amount-dimension', body ?? {}),
  /** POST /api/analytics/reports/list */
  reportsList: (body?: Record<string, unknown>) => request.post('/api/analytics/reports/list', body ?? {}),
  /** POST /api/analytics/reports/create */
  reportsCreate: (body?: Record<string, unknown>) => request.post('/api/analytics/reports/create', body ?? {}),
  /** POST /api/analytics/reports/detail */
  reportsDetail: (body: { id: string } | Record<string, unknown>) => request.post('/api/analytics/reports/detail', body),
  /** POST /api/analytics/reports/download */
  reportsDownload: (body: { id: string } | Record<string, unknown>) =>
    request.post('/api/analytics/reports/download', body, { responseType: 'blob' }),
  /** 兼容旧调用 */
  getReports: (params?: Record<string, unknown>) => request.post('/api/analytics/reports/list', params ?? {}),
  generateReport: (data: Record<string, unknown>) => request.post('/api/analytics/reports/create', data),
  downloadReport: (id: string) =>
    request.post('/api/analytics/reports/download', { id }, { responseType: 'blob' }),
  getAnalyticsData: (params?: Record<string, unknown>) =>
    request.post('/api/analytics/indicators', params ?? {}),
}

/** 用户 / 个人资料：改用 /api/settings/profile 与 /api/settings/security */
export const userApi = {
  /** POST /api/settings/profile/get */
  getProfile: (body?: Record<string, unknown>) =>
    request.post('/api/settings/profile/get', body ?? {}),
  /** POST /api/settings/profile/update */
  updateProfile: (data: Record<string, unknown>) =>
    request.post('/api/settings/profile/update', data),
  /** POST /api/settings/security/change-password */
  changePassword: (data: Record<string, unknown>) =>
    request.post('/api/settings/security/change-password', data),
}

/** 系统设置：改用 /api/settings/system */
export const systemApi = {
  /** POST /api/settings/system/get */
  getSettings: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/get', body ?? {}),
  /** POST /api/settings/system/update */
  updateSettings: (data: Record<string, unknown>) =>
    request.post('/api/settings/system/update', data),
  /** POST /api/settings/system/info */
  getStatus: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/info', body ?? {}),
  /** POST /api/settings/system/backup */
  backupData: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/backup', body ?? {}),
  /** POST /api/settings/system/check-update */
  checkUpdate: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/check-update', body ?? {}),
  /** POST /api/settings/system/reset-defaults */
  resetDefaults: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/reset-defaults', body ?? {}),
  /** 兼容旧的 restoreData，后端如无该接口可忽略调用 */
  restoreData: (data: Record<string, unknown>) =>
    request.post('/api/system/restore', data),
}

/** 设置中心：通知 & 安全类接口 */
export const settingsApi = {
  /** 通知设置：GET/UPDATE */
  notificationsGet: (body?: Record<string, unknown>) =>
    request.post('/api/settings/notifications/get', body ?? {}),
  notificationsUpdate: (data: Record<string, unknown>) =>
    request.post('/api/settings/notifications/update', data),
  /** 安全设置：GET/UPDATE/会话管理 */
  securityGet: (body?: Record<string, unknown>) =>
    request.post('/api/settings/security/get', body ?? {}),
  securityUpdate: (data: Record<string, unknown>) =>
    request.post('/api/settings/security/update', data),
  securitySessions: (body?: Record<string, unknown>) =>
    request.post('/api/settings/security/sessions', body ?? {}),
  securityRevokeSession: (data: Record<string, unknown>) =>
    request.post('/api/settings/security/revoke-session', data),
}

/** 通知：/api/notifications */
export const notificationApi = {
  getNotifications: (params?: Record<string, unknown>) =>
    request.get('/api/notifications', { params }),
  markAsRead: (id: string) => request.put(`/api/notifications/${id}/read`),
  markAllAsRead: () => request.put('/api/notifications/read-all'),
  deleteNotification: (id: string) => request.delete(`/api/notifications/${id}`),
  clearNotifications: () => request.delete('/api/notifications'),
}

// ========== 角色与权限管理 ==========

/** 权限类型：菜单权限控制侧栏/路由可见，按钮权限控制页面内操作按钮 */
export type PermissionType = 'menu' | 'button'

/** 权限定义（含类型：菜单 vs 按钮） */
export const PERMISSION_LIST: readonly { code: string; name: string; module: string; type: PermissionType }[] = [
  { code: 'dashboard:view', name: '仪表板', module: '仪表板', type: 'menu' },
  { code: 'data:view', name: '资产数据-访问', module: '资产数据', type: 'menu' },
  { code: 'data:export', name: '资产数据-导出', module: '资产数据', type: 'button' },
  { code: 'assets:view', name: '资产产品-访问', module: '资产产品', type: 'menu' },
  { code: 'assets:create', name: '资产产品-新增', module: '资产产品', type: 'button' },
  { code: 'assets:edit', name: '资产产品-编辑', module: '资产产品', type: 'button' },
  { code: 'assets:delete', name: '资产产品-删除', module: '资产产品', type: 'button' },
  { code: 'analytics:view', name: '分析报告-访问', module: '分析报告', type: 'menu' },
  { code: 'analytics:export', name: '分析报告-导出', module: '分析报告', type: 'button' },
  { code: 'settings:view', name: '系统设置-访问', module: '系统设置', type: 'menu' },
  { code: 'settings:edit', name: '系统设置-编辑', module: '系统设置', type: 'button' },
  { code: 'users:view', name: '账号管理-访问', module: '系统管理', type: 'menu' },
  { code: 'users:create', name: '账号管理-新增', module: '系统管理', type: 'button' },
  { code: 'users:edit', name: '账号管理-编辑', module: '系统管理', type: 'button' },
  { code: 'users:delete', name: '账号管理-删除', module: '系统管理', type: 'button' },
  { code: 'users:resetPwd', name: '账号管理-重置密码', module: '系统管理', type: 'button' },
  { code: 'users:manage', name: '账号管理-全部', module: '系统管理', type: 'menu' },
  { code: 'roles:view', name: '角色管理-访问', module: '系统管理', type: 'menu' },
  { code: 'roles:create', name: '角色管理-新增', module: '系统管理', type: 'button' },
  { code: 'roles:edit', name: '角色管理-编辑', module: '系统管理', type: 'button' },
  { code: 'roles:delete', name: '角色管理-删除', module: '系统管理', type: 'button' },
  { code: 'roles:manage', name: '角色管理-全部', module: '系统管理', type: 'menu' },
]

export type PermissionCode = (typeof PERMISSION_LIST)[number]['code']

/** 仅菜单权限码（用于侧栏/路由） */
export const MENU_PERMISSION_CODES = PERMISSION_LIST.filter((p) => p.type === 'menu').map((p) => p.code)

export interface RoleItem {
  id: string
  name: string
  code: string
  description?: string
  permissions: string[]
  status?: 'active' | 'disabled'
  createdAt?: string
  updatedAt?: string
}

export interface UserAccountItem {
  id: string
  username: string
  email?: string
  nickname?: string
  phone?: string
  roles: { id: string; name: string; code: string }[]
  status?: 'active' | 'disabled'
  lastLoginAt?: string
  createdAt?: string
}

/** POST /api/users/create 入参 */
export interface UserCreateParams {
  username: string
  password: string
  email?: string
  nickname?: string
  phone?: string
  status?: 'active' | 'disabled'
  roleIds?: string[]
}

/** POST /api/users/update 入参 */
export interface UserUpdateParams {
  id: string
  email?: string
  nickname?: string
  phone?: string
  status?: string
  roleIds?: string[]
}

/** POST /api/users/delete 入参 */
export interface UserDeleteParams {
  id: string
}

/** POST /api/users/reset-password 入参 */
export interface UserResetPasswordParams {
  id: string
  password: string
}

/** 列表请求通用入参（与后端文档 0.4 分页约定一致） */
export interface ListParams {
  page?: number
  pageSize?: number
  filters?: { keyword?: string; [k: string]: unknown }
  [k: string]: unknown
}

/** 列表响应 data 结构（与后端文档 0.4 一致） */
export interface ListResponseData<T> {
  items: T[]
  total?: number
  page?: number
  pageSize?: number
}

/** POST /api/roles/list 入参 */
export interface RoleListParams {
  keyword?: string
  page?: number
  pageSize?: number
}

/** POST /api/roles/create 入参 */
export interface RoleCreateParams {
  name: string
  code: string
  description?: string
  permissions?: string[]
  status?: 'active' | 'disabled'
}

/** POST /api/roles/update 入参 */
export interface RoleUpdateParams {
  id: string
  name?: string
  code?: string
  description?: string
  permissions?: string[]
  status?: string
}

/** POST /api/roles/delete 入参 */
export interface RoleDeleteParams {
  id: string
}

/** 角色/权限配置用：菜单与按钮列表项（/api/menus/list-for-role） */
export interface MenuForRoleItem {
  code: string
  name: string
  module: string
  type: 'menu' | 'button'
}

/** 菜单列表接口（用于角色新增/编辑时拉取全部可分配权限，与后端文档一致：全部 POST） */
export const menusApi = {
  /** POST /api/menus/list-for-role - 角色可分配的菜单与按钮列表 */
  listForRole: (body?: Record<string, unknown>) =>
    request.post<{ success?: boolean; code?: string; message?: string; data?: { items?: MenuForRoleItem[] } }>(
      '/api/menus/list-for-role',
      body ?? {}
    ),
}

/** 角色管理 API（与 backend-api-dev.md 约定一致：全部 POST） */
export const roleApi = {
  /** POST /api/roles/list - 角色列表，入参：keyword?, page?, pageSize? */
  list: (body?: RoleListParams) =>
    request.post<{ success?: boolean; code?: string; message?: string; data?: ListResponseData<unknown> }>(
      '/api/roles/list',
      body ?? {}
    ),
  /** POST /api/roles/detail - 角色详情 */
  detail: (body: { id: string }) => request.post('/api/roles/detail', body),
  /** POST /api/roles/create - 创建角色 */
  create: (data: RoleCreateParams) => request.post('/api/roles/create', data),
  /** POST /api/roles/update - 更新角色 */
  update: (data: RoleUpdateParams) => request.post('/api/roles/update', data),
  /** POST /api/roles/delete - 删除角色 */
  delete: (body: RoleDeleteParams) => request.post('/api/roles/delete', body),
}

/** POST /api/users/list 入参 */
export interface UserListParams {
  keyword?: string
  page?: number
  pageSize?: number
}

/** 账号管理 API（用户管理，与 backend-api-dev.md 约定一致：全部 POST） */
export const userManageApi = {
  /** POST /api/users/list - 用户账号列表，入参：keyword?, page?, pageSize? */
  list: (body?: UserListParams) =>
    request.post<{ success?: boolean; code?: string; message?: string; data?: ListResponseData<unknown> }>(
      '/api/users/list',
      body ?? {}
    ),
  /** POST /api/users/detail - 用户详情 */
  detail: (body: { id: string }) => request.post('/api/users/detail', body),
  /** POST /api/users/create - 创建用户，入参：username, password, email?, nickname?, phone?, status?, roleIds? */
  create: (data: UserCreateParams) => request.post('/api/users/create', data),
  /** POST /api/users/update - 更新用户，入参：id, email?, nickname?, phone?, status?, roleIds? */
  update: (data: UserUpdateParams) => request.post('/api/users/update', data),
  /** POST /api/users/delete - 删除用户，入参：id */
  delete: (body: UserDeleteParams) => request.post('/api/users/delete', body),
  /** POST /api/users/reset-password - 重置密码，入参：id, password */
  resetPassword: (data: UserResetPasswordParams) =>
    request.post('/api/users/reset-password', data),
}

/** 上传：/api/upload */
export const uploadApi = {
  uploadFile: (formData: FormData) =>
    request.post('/api/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getUploadProgress: (id: string) => request.get(`/api/upload/${id}/progress`),
  cancelUpload: (id: string) => request.delete(`/api/upload/${id}`),
}

export default request
